import {
  kisiQueryKeys,
  useMutationKisiInitialize,
  useMutationKisiSaveApiKey
} from "@gymflow/api";
import { KisiPlace } from "@gymflow/types";
import { infiniteQueryOptions } from "@tanstack/react-query";
import { FormikProvider, useFormik } from "formik";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import {
  ModalContext,
  ModalWrapper
} from "../../../../providers";
import { ToastContext } from "../../../../providers/ToastProvider/context";
import useGymflowModels from "../../../../store";
import { Button, DangerIcon, FormInput, FormSelectInput } from "../../../atoms";
import { XIcon } from "../../../atoms/icons/XIcon";

const EnableKisiSchema = z.object({
  apiKey: z.string().length(32),
  kisiPlace: z.object({
    id: z.string(),
    label: z.string(),
    value: z.custom<KisiPlace>(),
  }),
});

export const KisiIntegrationEnableModal = () => {
  const { hide } = useContext(ModalContext);
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const kisiInitializeMutation = useMutationKisiInitialize({ api });
  const { toast } = useContext(ToastContext);
  const formik = useFormik({
    initialValues: {
      apiKey: "",
      kisiPlace: undefined as unknown as z.infer<
        typeof EnableKisiSchema
      >["kisiPlace"],
    },
    validationSchema: toFormikValidationSchema(EnableKisiSchema),
    validate: toFormikValidate(EnableKisiSchema),
    onSubmit: async (values) => {
      await kisiInitializeMutation.mutateAsync({
        kisiPlaceId: values.kisiPlace.id,
        membershipToGroupMap: {},
        facilityToGroupMap: {},
      });
      toast({
        message: t("components.kisiIntegrationEnableModal.success"),
        description: t("components.kisiIntegrationEnableModal.successDescription"),
        intent: "success",
      });
      hide();
    },
  });
  const { mutateAsync: saveApiKey } = useMutationKisiSaveApiKey({ api });
  const isValidApiKey =
    formik.values.apiKey && formik.errors.apiKey === undefined;
  useEffect(() => {
    if (isValidApiKey) {
      saveApiKey({
        apiKey: formik.values.apiKey,
      });
    }
  }, [formik.values.apiKey, saveApiKey, isValidApiKey]);
  return (
    <ModalWrapper
      className="flex w-full max-w-xl flex-col gap-6"
      onCancel={() => hide()}
    >
      <FormikProvider value={formik}>
        <div className="flex flex-row justify-between">
          <div className="bg-secondary-50 flex size-11 items-center justify-center rounded-full">
            <div className="bg-secondary-100 flex size-8 items-center justify-center self-center rounded-full">
              <DangerIcon
                className="size-5"
                pathClassName="stroke-secondary-600"
              />
            </div>
          </div>
          <div className="cursor-pointer" onClick={() => hide()}>
            <XIcon className="size-6" pathClassName="stroke-gray-600" />
          </div>
        </div>
        <div className="relative flex flex-col gap-5">
          <div className="flex flex-col">
            <div className="text-lg font-semibold text-gray-950">
              {t("components.kisiIntegrationEnableModal.title")}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t("components.kisiIntegrationEnableModal.description")}
            </div>
          </div>
          <FormInput
            name="apiKey"
            label={t("components.kisiIntegrationEnableModal.apiKey")}
            placeholder={t(
              "components.kisiIntegrationEnableModal.apiKeyPlaceholder",
            )}
            isRequired
          />
          <div className="text-sm font-medium text-gray-500">
            {t("components.kisiIntegrationEnableModal.kisiApiKeyHelp")}
          </div>
          <FormSelectInput
            name="kisiPlace"
            label={t("components.kisiIntegrationEnableModal.kisiPlace")}
            placeholder={t(
              "components.kisiIntegrationEnableModal.kisiPlacePlaceholder",
            )}
            isRequired
            disabled={!isValidApiKey}
            dataFetchingQuery={() => {
              return infiniteQueryOptions({
                queryKey: kisiQueryKeys.places(),
                queryFn: async () => (await api.kisiApi.fetchPlaces()).data,
                select: (data) => {
                  return {
                    pageParams: data.pageParams,
                    pages: data.pages.map((page) =>
                      page.map((place) => ({
                        id: place.id,
                        label: place.name,
                        value: place,
                      })),
                    ),
                  };
                },
                getNextPageParam: () => undefined,
                initialPageParam: undefined,
                enabled: !!formik.values.apiKey,
              });
            }}
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-4">
          <Button
            intent="secondary"
            className="!px-14"
            onClick={async () => {
              await formik.submitForm();
            }}
          >
            {t("portal.common.setup")}
          </Button>
        </div>
      </FormikProvider>
    </ModalWrapper>
  );
};
